/**
 * apps/command/src/screens/ChannelsScreen.tsx — one row per distributable
 * surface: the kiosk, the mobile app, the Telegram bot, the Telegram Mini
 * App. Enrolled, active in the last 30 days, its version spread where a
 * version concept applies, and how many murojaats came through it.
 *
 * ============================================================================
 * "SEND AN UPDATE" MEANS SOMETHING DIFFERENT ON EVERY ROW, AND THE SCREEN
 * SAYS SO INSTEAD OF PRETENDING OTHERWISE.
 * ============================================================================
 *
 * `kiosk` is the only card with a real push: §4.5's command bus already
 * reaches every kiosk, and `BulkScreen` already carries the whole two-person
 * flow for touching many of them at once (§10). This screen's button does not
 * duplicate that — it hands off to `/bulk?type=update.install` with the verb
 * pre-selected, so approval, the frozen target set and the appeal-lock defer
 * all still run through the one path that has them.
 *
 * `mobile` has no store integration and no in-app updater: what is real is a
 * field-observed version, not a claim about what Google or Apple are serving,
 * and the card says so rather than offering a button that cannot push
 * anything.
 *
 * `bot` and `webapp` run server-side. There is no client binary a citizen
 * could be behind on — an update is a deploy, not something sent to anyone —
 * so neither a version nor a push control is invented for them.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { t } from '../i18n/index.js';
import { stamp } from '../lib/format.js';
import { Button, Notice, Panel, PanelHeader, Skeleton } from '../ui/index.jsx';
import { Distribution, PageHeader } from '../ui/console.jsx';
import {
  IconChannelBot,
  IconChannelKiosk,
  IconChannelMobile,
  IconChannelWebapp,
  IconPushUpdate,
} from '../ui/icons.jsx';

type ChannelsOverview = components['schemas']['MbtChannelsOverview'];
type ChannelStats = components['schemas']['MbtChannelStats'];

const CARD: Record<
  ChannelStats['client_app'],
  { Icon: (props: { className?: string }) => React.JSX.Element; titleKey: string }
> = {
  kiosk: { Icon: IconChannelKiosk, titleKey: 'channel.kiosk' },
  mobile: { Icon: IconChannelMobile, titleKey: 'channel.mobile' },
  bot: { Icon: IconChannelBot, titleKey: 'channel.bot' },
  webapp: { Icon: IconChannelWebapp, titleKey: 'channel.webapp' },
};

/** Fixed, deliberately: kiosk leads because it is the one with a real push. */
const ORDER: readonly ChannelStats['client_app'][] = ['kiosk', 'mobile', 'bot', 'webapp'];

const PLATFORM_LABEL: Record<string, string> = {
  android: 'Android',
  ios: 'iOS',
};

function versionLabel(row: { version: string; platform?: string }): string {
  return row.platform ? `${PLATFORM_LABEL[row.platform] ?? row.platform} · ${row.version}` : row.version;
}

/** One number in a card's stat strip. Reads plainly, never a claim about trend. */
function Stat({ label, value }: { label: string; value: number }): React.JSX.Element {
  return (
    <div className="min-w-0">
      <p className="truncate text-micro uppercase tracking-wide text-ink-500">{label}</p>
      <p className="mt-0.5 text-figure font-bold tabular-nums leading-none text-ink-100">
        {value}
      </p>
    </div>
  );
}

function ChannelCard({ row, mayIssue }: { row: ChannelStats; mayIssue: boolean }): React.JSX.Element {
  const { Icon, titleKey } = CARD[row.client_app];
  const hasVersions = row.versions.length > 0;

  return (
    <Panel>
      <PanelHeader
        title={t(titleKey)}
        aside={
          <span className="flex items-center gap-2 text-ink-500">
            <Icon className="h-4 w-4" />
          </span>
        }
      />
      <div className="space-y-4 px-4 py-4">
        <div className="grid grid-cols-3 gap-3">
          <Stat label={t('fleet.enrolled')} value={row.enrolled} />
          <div className="border-l border-hairline pl-3">
            <Stat label={t('channels.active30d')} value={row.active_30d} />
          </div>
          <div className="border-l border-hairline pl-3">
            <Stat label={t('channels.submissions30d')} value={row.submissions_30d} />
          </div>
        </div>

        {row.latest_version_source === 'not_applicable' ? (
          <Notice tone="quiet">{t('channels.noClientVersion')}</Notice>
        ) : (
          <>
            {hasVersions ? (
              <Distribution
                title={t('fleet.versionSpread')}
                rows={row.versions.map((version) => ({
                  key: versionLabel(version),
                  label: versionLabel(version),
                  value: version.count,
                }))}
              />
            ) : (
              <p className="text-micro text-ink-500">{t('app.notYet')}</p>
            )}

            <div className="flex flex-wrap items-end justify-between gap-3 border-t border-hairline pt-3">
              <div className="min-w-0">
                <p className="text-micro text-ink-500">{t('channels.latestVersion')}</p>
                <p className="text-label font-semibold tabular-nums text-ink-100">
                  {row.latest_known_version ?? t('app.unknown')}
                </p>
                <p className="text-nano text-ink-500">
                  {t(
                    row.latest_version_source === 'published'
                      ? 'channels.sourcePublished'
                      : 'channels.sourceFieldObserved',
                  )}
                </p>
              </div>

              {row.updateable ? (
                mayIssue ? (
                  <Link to="/bulk?type=update.install">
                    <Button className="flex items-center gap-2">
                      <IconPushUpdate className="h-4 w-4" />
                      {t('channels.pushUpdate')}
                    </Button>
                  </Link>
                ) : (
                  <p className="max-w-[14rem] text-right text-micro text-ink-500">
                    {t('channels.noPushPermission')}
                  </p>
                )
              ) : (
                <p className="max-w-[14rem] text-right text-micro text-ink-500">
                  {t('channels.noPushManual')}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

export function ChannelsScreen(): React.JSX.Element {
  const { expire, allows } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const mayIssue = allows('mbt.command.issue');

  const [overview, setOverview] = useState<ChannelsOverview | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const data = unwrap(await api().GET('/mbt/channels/overview'));
        if (!cancelled) {
          setOverview(data as ChannelsOverview);
          setFailed(false);
        }
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) onExpired();
        // The previous figures stay on screen (§15.7's rule, applied here
        // too): a transient poll failure must not blank a screen that had
        // real numbers on it a moment ago.
        else setFailed(true);
      }
    };
    void load();
    // Five minutes: version and install counts move slowly, and this is not
    // a fleet-health screen an operator watches for the next event.
    const timer = setInterval(() => void load(), 300_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [onExpired]);

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={<span>{t('channels.eyebrow')}</span>}
        title={t('nav.channels')}
        subtitle={t('channels.subtitle')}
        right={
          overview ? (
            <p className="text-micro tabular-nums text-ink-500">
              {t('app.asOf', { at: stamp(new Date(overview.as_of)) })}
            </p>
          ) : undefined
        }
      />

      {failed ? <Notice tone="warn">{t('error.network')}</Notice> : null}

      {overview?.scoped ? <Notice tone="quiet">{t('overview.scopedNote')}</Notice> : null}

      {overview ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {ORDER.map((clientApp) => {
            const row = overview.channels.find((entry) => entry.client_app === clientApp);
            return row ? <ChannelCard key={clientApp} row={row} mayIssue={mayIssue} /> : null;
          })}
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      )}
    </div>
  );
}
